import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from './button';
import { Heading } from './heading';
import { Text } from './text';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  XCircleIcon,
  UserIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon
} from '@heroicons/react/24/solid';
import { NotifyFianzaDialog } from './NotifyFianzaDialog';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { doc, onSnapshot, getDoc, updateDoc, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { UploadFianza } from './UploadFianza';
import { FianzaView } from './FianzaView';
import { ProcessedFileView } from './ProcessedFileView';
import { ContratoDocumentos } from './ContratoDocumentos';
import { ContratoTimeline } from './ContratoTimeline';
import PendientesPanel from './PendientesPanel';
import OtActivas from './ot/OtActivas';
import { PENDIENTE_TIPOS } from '../utils/pendientes';
import { useRegistrarContextoPendiente } from '../contexts/PendienteContexto';
import { reprocessFile } from '../api/documentUpload';
import { Dialog } from './dialog';
import AssignedUsersBubbles from './AssignedUsersBubbles';
import UserAssignmentSelector from './UserAssignmentSelector';
import { useContracts } from '../contexts/DataContext';
import { useUser } from './UserContext';
import { usePortalConfig } from '../hooks/usePortalConfig';
import { logBitacora } from '../utils/bitacora';
import { setBeneficiarioConvenio } from '../api/comisiones';
import { namesMatch, getFianzaStatus } from '../utils/convenioCompliance';
import { formatNumeroFianza } from '../utils/calculations';
import { usesProcesamiento } from '../utils/contratoStorage';

/**
 * ContratoView Component
 * Displays detailed contract information with fianzas requeridas and emitidas
 * Shows client information and contract PDF
 */
export function ContratoView({ isOpen, onClose, contrato, clientData, onUpdate, convenio: convenioProp, beneficiariosById: beneficiariosByIdProp }) {
  const [liveContratoData, setLiveContratoData] = useState(contrato);
  const [contractPdfUrl, setContractPdfUrl] = useState(null);
  const [contractFileData, setContractFileData] = useState(null);
  const [fetchedConvenio, setFetchedConvenio] = useState(null);
  const [isUploadFianzaOpen, setIsUploadFianzaOpen] = useState(false);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);
  const [selectedFianza, setSelectedFianza] = useState(null);
  // Quick-send: re-email an emitted fianza from the contrato view.
  const [notifyFianza, setNotifyFianza] = useState(null); // {contratoId, fianzaId, numeroFianza}
  const [isProcessedViewOpen, setIsProcessedViewOpen] = useState(false);
  const [processedViewData, setProcessedViewData] = useState({});
  const [requiredEditable, setRequiredEditable] = useState([]);
  const [savingRequired, setSavingRequired] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState('success');
  const [isContractAssignmentOpen, setIsContractAssignmentOpen] = useState(false);
  const [savingBeneficiarioConvenio, setSavingBeneficiarioConvenio] = useState(false);
  const { updateContractAssignedUsers } = useContracts();
  const { user, userData, isClient } = useUser();
  const { hasTarifa } = usePortalConfig();

  // Tell the bolita what is on screen, so a pendiente filed from it starts
  // attached to this contrato.
  useRegistrarContextoPendiente(Boolean(isOpen && !isClient && liveContratoData?.id), {
    clienteId: clientData?.id || contrato?.clientId || null,
    convenioId: liveContratoData?.convenioId || null,
    contratoId: liveContratoData?.id || null,
    numeroContrato: liveContratoData?.numeroContrato?.value || '',
    contratoTitulo: liveContratoData?.tituloContrato?.value || '',
  });

  // Helper function to safely access contract field values
  const getFieldValue = (field, defaultValue = 'N/A') => {
    if (!field || field.value === null || field.value === undefined || field.value === '') {
      return defaultValue;
    }
    return field.value;
  };

  // Set up live listener for contract updates
  useEffect(() => {
    if (!isOpen || !contrato?.id) return;

    const contractDocRef = doc(db, 'contracts', contrato.id);

    const unsubscribe = onSnapshot(contractDocRef, (contractDoc) => {
      if (contractDoc.exists()) {
        const contractData = contractDoc.data();
        setLiveContratoData({ ...contractData, id: contractDoc.id });
      }
    }, (error) => {
      console.error('Error listening to contract updates:', error);
    });

    return () => unsubscribe();
  }, [isOpen, contrato?.id]);

  const isConvenioContract = Boolean(liveContratoData?.convenioId);
  // Convenio and directo (no link) contracts keep their processing results
  // in contracts/{id}/procesamiento; link contracts in links/{linkId}/files.
  const hasProcesamiento = usesProcesamiento(liveContratoData);

  // Contract-level beneficiario-convenio marker: the backend flips the flag
  // and re-prices every fianza's pending comisión movements in one transaction.
  const handleToggleBeneficiarioConvenio = async () => {
    try {
      setSavingBeneficiarioConvenio(true);
      const nuevo = !liveContratoData?.beneficiarioConvenio;
      await setBeneficiarioConvenio({
        contractId: contrato.id,
        beneficiarioConvenio: nuevo,
      });
      setLiveContratoData((prev) => ({ ...prev, beneficiarioConvenio: nuevo }));
      await logBitacora({
        action: 'UPDATE',
        collection: 'contracts',
        documentId: contrato.id,
        changes: [{
          field: 'beneficiarioConvenio',
          oldValue: !nuevo,
          newValue: nuevo,
        }],
        metadata: {
          contratoId: contrato.id,
          clientId: contrato.clientId || null,
          description: `Marcó el contrato ${nuevo ? 'con' : 'sin'} convenio de proveedores del beneficiario`,
        },
        user,
        userData,
      });
      if (onUpdate) await onUpdate();
    } catch (error) {
      console.error('Error toggling beneficiario convenio (contrato):', error);
      setAlertType('error');
      setAlertMessage(error.message || 'Error al actualizar el marcador de convenio');
    } finally {
      setSavingBeneficiarioConvenio(false);
    }
  };

  // Fetch convenio data if not passed as prop (e.g. opened from Pulso/Contratos page)
  useEffect(() => {
    if (!isOpen || !isConvenioContract || convenioProp) {
      setFetchedConvenio(null);
      return;
    }

    const fetchConvenio = async () => {
      try {
        const convenioDoc = await getDoc(doc(db, 'convenios', liveContratoData.convenioId));
        if (convenioDoc.exists()) {
          setFetchedConvenio({ id: convenioDoc.id, ...convenioDoc.data() });
        }
      } catch (err) {
        console.error('Error fetching convenio for ContratoView:', err);
      }
    };

    fetchConvenio();
  }, [isOpen, isConvenioContract, liveContratoData?.convenioId, convenioProp]);

  // Resolve convenio and beneficiariosById from prop or fetched data
  const convenio = convenioProp || fetchedConvenio;
  const derivedBeneficiariosById = useMemo(() => {
    if (beneficiariosByIdProp) return beneficiariosByIdProp;
    if (!convenio?.beneficiarios) return {};
    const map = {};
    convenio.beneficiarios.forEach((b) => { map[b.id] = b; });
    return map;
  }, [beneficiariosByIdProp, convenio?.beneficiarios]);
  const beneficiariosById = derivedBeneficiariosById;

  // Build bitácora metadata that routes correctly to the convenio Historial
  // when this contract belongs to a convenio.
  const buildBitacoraMeta = useCallback((description, extra = {}) => {
    if (isConvenioContract) {
      return {
        convenioId: liveContratoData?.convenioId,
        convenioName: convenio?.nombre || null,
        beneficiarioNombre: beneficiariosById?.[liveContratoData?.beneficiarioId]?.nombre || null,
        proveedor: liveContratoData?.proveedor?.value || null,
        contratoId: liveContratoData?.id,
        description,
        ...extra,
      };
    }
    return {
      clientId: contrato?.clientId || clientData?.id || null,
      clientName: clientData?.nombreEmpresa || 'Sin nombre',
      contratoId: liveContratoData?.id,
      description,
      ...extra,
    };
  }, [isConvenioContract, liveContratoData, convenio, beneficiariosById, contrato, clientData]);

  // Function to load contract file data
  const loadContractFileData = useCallback(async () => {
    if (!liveContratoData?.id) {
      setContractFileData(null);
      return;
    }

    try {
      if (hasProcesamiento) {
        // Convenio / directo contract: read from contracts/{contractId}/procesamiento (latest doc)
        const procQuery = query(
          collection(db, 'contracts', liveContratoData.id, 'procesamiento'),
          orderBy('uploadedAt', 'desc'),
          limit(1)
        );
        const procSnap = await getDocs(procQuery);
        if (!procSnap.empty) {
          const procDoc = procSnap.docs[0];
          setContractFileData({ ...procDoc.data(), fileDocId: procDoc.id });
        }
      } else {
        // Link-based contract: read from links/{linkId}/files/{fileDocId}
        if (!liveContratoData.linkId) {
          setContractFileData(null);
          return;
        }
        const timestamp = liveContratoData.id.split('_')[1];
        if (!timestamp) return;

        const fileDocId = `contrato_${timestamp}`;
        const fileDocRef = doc(db, 'links', liveContratoData.linkId, 'files', fileDocId);
        const fileDocSnap = await getDoc(fileDocRef);

        if (fileDocSnap.exists()) {
          setContractFileData(fileDocSnap.data());
        }
      }
    } catch (error) {
      console.error('Error loading contract file data:', error);
    }
  }, [liveContratoData?.id, liveContratoData?.linkId, hasProcesamiento]);

  // Load contract file data from files subcollection
  useEffect(() => {
    if (!isOpen) {
      setContractFileData(null);
      return;
    }

    loadContractFileData();
  }, [isOpen, loadContractFileData]);

  // Load contract PDF URL
  useEffect(() => {
    if (!isOpen) {
      setContractPdfUrl(null);
      return;
    }

    const loadPdfUrl = async () => {
      try {
        // For convenio contracts, get storagePath from the procesamiento doc
        let storagePath = liveContratoData?.fileUrl;
        if (!storagePath && contractFileData?.storagePath) {
          storagePath = contractFileData.storagePath;
        }
        if (!storagePath) {
          setContractPdfUrl(null);
          return;
        }
        const fileRef = ref(storage, storagePath);
        const url = await getDownloadURL(fileRef);
        setContractPdfUrl(url);
      } catch (error) {
        console.error('Error loading contract PDF:', error);
        setContractPdfUrl(null);
      }
    };

    loadPdfUrl();
  }, [isOpen, liveContratoData?.fileUrl, contractFileData?.storagePath]);

  // Hide body scroll when view is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Get status color
  const getStatusColor = (estatus) => {
    switch (estatus) {
      case 'activo': return 'bg-green-100 text-green-800';
      case 'terminado': return 'bg-blue-100 text-blue-800';
      case 'cancelado': return 'bg-red-100 text-red-800';
      case 'suspendido': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFianzaStatusColor = (estatus) => {
    switch (estatus) {
      case 'vigente': return 'bg-green-100 text-green-800';
      case 'emitida': return 'bg-blue-100 text-blue-800';
      case 'pendiente': return 'bg-orange-100 text-orange-800';
      case 'en_proceso': return 'bg-yellow-100 text-yellow-800';
      case 'postergada': return 'bg-purple-100 text-purple-800';
      case 'vencida': return 'bg-red-100 text-red-800';
      case 'liberada': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get contract error message from contract file data
  const getContractErrorMessage = () => {
    return contractFileData?.results?.errors?.errorMessage?.value || null;
  };

  const titulo = getFieldValue(liveContratoData.tituloContrato, 'Sin título');
  const tipo = getFieldValue(liveContratoData.tipoContrato, 'Tipo no especificado');
  const estatus = getFieldValue(liveContratoData.estatus, 'pendiente');
  const contratante = getFieldValue(liveContratoData.contratante, 'No especificado');
  const proveedor = getFieldValue(liveContratoData.proveedor, null);
  const montoValue = getFieldValue(liveContratoData.montoContrato, null);
  const fechaInicioValue = getFieldValue(liveContratoData.fechaInicio, null);
  const fechaTerminoValue = getFieldValue(liveContratoData.fechaTermino, null);

  const monto = montoValue !== null && montoValue !== 'N/A'
    ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(montoValue)
    : 'N/A';
  const fechaInicio = fechaInicioValue || 'N/A';
  const fechaTermino = fechaTerminoValue || 'N/A';

  const fianzasRequeridas = useMemo(
    () => liveContratoData.fianzasRequeridas?.value || [],
    [liveContratoData.fianzasRequeridas]
  );
  useEffect(() => {
    setRequiredEditable(fianzasRequeridas);
  }, [fianzasRequeridas]);

  // Los inputs entregan strings. Se normalizan aquí, al guardar, y no en cada
  // tecla: coercionar mientras se escribe impide teclear "1.5" o borrar el
  // campo. Un requisito con monto string rompe los rollups del proveedor, que
  // suman con `+=` y terminan concatenando en vez de sumar.
  const normalizeRequisito = (fr) => {
    const toNumber = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    return { ...fr, monto: toNumber(fr.monto), porcentaje: toNumber(fr.porcentaje) };
  };

  const persistFianzasRequeridas = async (updatedList) => {
    if (!liveContratoData?.id) return;
    setSavingRequired(true);
    try {
      const oldList = liveContratoData.fianzasRequeridas?.value || [];
      updatedList = (updatedList || []).map(normalizeRequisito);

      await updateDoc(doc(db, "contracts", liveContratoData.id), {
        "fianzasRequeridas.value": updatedList,
        updatedAt: Date.now(),
      });

      // Determine what changed and log appropriately
      if (updatedList.length > oldList.length) {
        // Item(s) added - log the new item(s)
        for (let i = oldList.length; i < updatedList.length; i++) {
          const newItem = updatedList[i];
          const tipo = newItem.tipo || 'Sin tipo';

          // Build detailed description
          const details = [];
          if (newItem.porcentaje) {
            details.push(`${newItem.porcentaje}% del contrato`);
          }
          if (newItem.monto) {
            details.push(`$${new Intl.NumberFormat('es-MX').format(newItem.monto)}`);
          }
          if (newItem.estatus && newItem.estatus !== 'pendiente') {
            details.push(`estatus: ${newItem.estatus}`);
          }

          const detailStr = details.length > 0 ? ` (${details.join(', ')})` : '';

          try {
            await logBitacora({
              action: 'UPDATE',
              collection: 'contracts',
              documentId: liveContratoData.id,
              changes: [{
                field: 'fianzasRequeridas.value',
                oldValue: oldList,
                newValue: updatedList,
              }],
              metadata: buildBitacoraMeta(`Agregó fianza requerida '${tipo}'${detailStr}`),
              user,
              userData,
            });
          } catch (logError) {
            console.error('Failed to log fianza requerida addition:', logError);
          }
        }
      } else if (updatedList.length < oldList.length) {
        // Item(s) deleted - identify which ones
        const deletedItems = oldList.filter((oldItem, idx) => !updatedList[idx] ||
          JSON.stringify(oldItem) !== JSON.stringify(updatedList[idx]));

        for (const deletedItem of deletedItems) {
          const tipo = deletedItem.tipo || 'Sin tipo';
          const details = [];
          if (deletedItem.porcentaje) {
            details.push(`${deletedItem.porcentaje}%`);
          }
          if (deletedItem.monto) {
            details.push(`$${new Intl.NumberFormat('es-MX').format(deletedItem.monto)}`);
          }
          const detailStr = details.length > 0 ? ` (${details.join(', ')})` : '';

          try {
            await logBitacora({
              action: 'UPDATE',
              collection: 'contracts',
              documentId: liveContratoData.id,
              changes: [{
                field: 'fianzasRequeridas.value',
                oldValue: oldList,
                newValue: updatedList,
              }],
              metadata: buildBitacoraMeta(`Eliminó fianza requerida '${tipo}'${detailStr}`),
              user,
              userData,
            });
          } catch (logError) {
            console.error('Failed to log fianza requerida deletion:', logError);
          }
        }
      } else {
        // Same length - check for edits to existing items
        let hasChanges = false;
        for (let i = 0; i < oldList.length; i++) {
          const oldItem = oldList[i];
          const newItem = updatedList[i];

          // Collect what changed
          const changedFields = [];

          if (oldItem.tipo !== newItem.tipo) {
            changedFields.push(`tipo de '${oldItem.tipo || 'Sin tipo'}' a '${newItem.tipo || 'Sin tipo'}'`);
          }
          if (oldItem.porcentaje !== newItem.porcentaje) {
            changedFields.push(`porcentaje de ${oldItem.porcentaje || 0}% a ${newItem.porcentaje || 0}%`);
          }
          if (oldItem.monto !== newItem.monto) {
            const oldMonto = oldItem.monto ? `$${new Intl.NumberFormat('es-MX').format(oldItem.monto)}` : '$0';
            const newMonto = newItem.monto ? `$${new Intl.NumberFormat('es-MX').format(newItem.monto)}` : '$0';
            changedFields.push(`monto de ${oldMonto} a ${newMonto}`);
          }
          if (oldItem.estatus !== newItem.estatus) {
            changedFields.push(`estatus de '${oldItem.estatus || 'pendiente'}' a '${newItem.estatus || 'pendiente'}'`);
          }

          if (changedFields.length > 0) {
            hasChanges = true;
            const tipo = newItem.tipo || oldItem.tipo || 'Sin tipo';
            const description = `Actualizó ${changedFields.join(', ')} en fianza requerida '${tipo}'`;

            try {
              await logBitacora({
                action: 'UPDATE',
                collection: 'contracts',
                documentId: liveContratoData.id,
                changes: [{
                  field: `fianzasRequeridas.value[${i}]`,
                  oldValue: oldItem,
                  newValue: newItem,
                }],
                metadata: buildBitacoraMeta(description),
                user,
                userData,
              });
            } catch (logError) {
              console.error('Failed to log fianza requerida update:', logError);
            }
          }
        }

        // If no changes detected at all, log a generic update
        if (!hasChanges) {
          try {
            await logBitacora({
              action: 'UPDATE',
              collection: 'contracts',
              documentId: liveContratoData.id,
              changes: [{
                field: 'fianzasRequeridas.value',
                oldValue: oldList,
                newValue: updatedList,
              }],
              metadata: buildBitacoraMeta('Modificó fianzas requeridas del contrato'),
              user,
              userData,
            });
          } catch (logError) {
            console.error('Failed to log fianzas requeridas update:', logError);
          }
        }
      }
    } catch (err) {
      console.error("Error updating fianzas requeridas", err);
    } finally {
      setSavingRequired(false);
    }
  };

  const handleRequiredChange = (index, field, value) => {
    setRequiredEditable((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddRequired = () => {
    setRequiredEditable((prev) => {
      const next = [...prev, { tipo: "", porcentaje: "", monto: "", estatus: "pendiente", momento: "antes_inicio", postergadaHasta: null, fianzaId: null }];
      setEditingIndex(next.length - 1);
      return next;
    });
  };

  const handleDeleteRequired = async (index) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta fianza requerida?')) return;
    const updated = requiredEditable.filter((_, i) => i !== index);
    setRequiredEditable(updated);
    await persistFianzasRequeridas(updated);
  };

  const handleSaveRequired = async () => {
    await persistFianzasRequeridas(requiredEditable);
    setEditingIndex(null);
  };

  const handleCancelRequired = () => {
    if (editingIndex !== null && editingIndex >= fianzasRequeridas.length) {
      // Canceling a newly added card: remove it
      setRequiredEditable((prev) => prev.filter((_, i) => i !== editingIndex));
    } else {
      setRequiredEditable(fianzasRequeridas);
    }
    setEditingIndex(null);
  };

  // Handle contract reprocessing
  const handleReprocessContract = async () => {
    if (!window.confirm('¿Estás seguro de que deseas reprocesar el contrato? Esto volverá a analizar el documento y reescribirá los datos existentes. Esta operación no se puede deshacer.')) {
      return;
    }

    if (!liveContratoData?.id || !liveContratoData?.linkId) {
      alert('No se pudo obtener la información del contrato.');
      return;
    }

    // Extract timestamp from contract ID
    const timestamp = liveContratoData.id.split('_')[1];
    if (!timestamp) {
      alert('No se pudo obtener el ID del archivo.');
      return;
    }

    setIsReprocessing(true);

    try {
      const result = await reprocessFile(
        clientData?.id,
        liveContratoData.linkId,
        'contrato',
        timestamp
      );

      if (result.status === 'success') {
        setAlertMessage('Contrato reprocesado exitosamente');
        setAlertType('success');
        setTimeout(() => setAlertMessage(null), 3000);

        // Log reprocessing (non-blocking)
        try {
          await logBitacora({
            action: 'UPDATE',
            collection: 'contracts',
            documentId: liveContratoData.id,
            metadata: buildBitacoraMeta('Reprocesó contrato'),
            user,
            userData,
          });
        } catch (logError) {
          console.error('Failed to log contract reprocessing:', logError);
        }

        // Refetch contract file data to get updated results
        await loadContractFileData();
      } else {
        throw new Error(result.error || 'Error reprocessing contract');
      }
    } catch (error) {
      console.error('Error reprocessing contract:', error);
      setAlertMessage(`Error al reprocesar contrato: ${error.message}`);
      setAlertType('error');
      setTimeout(() => setAlertMessage(null), 5000);
    } finally {
      setIsReprocessing(false);
    }
  };

  const fianzasEmitidas = liveContratoData.fianzas || [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-hidden">
      {/* Header */}
      <div className={`text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between shadow-lg ${
        isConvenioContract ? 'bg-gradient-to-r from-[#53B5CC] to-azul-surelia' : 'bg-azul-surelia'
      }`}>
        <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
          <Button
            color="white"
            onClick={onClose}
            className="flex items-center gap-1 sm:gap-2 flex-shrink-0"
          >
            <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Volver</span>
          </Button>
          <div className="flex-1 min-w-0">
            {isConvenioContract && (
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-white/20 text-white">
                  CONVENIO
                </span>
                {liveContratoData?.proyectoId && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/15 text-white">
                    {(convenio?.proyectos || []).find((p) => p.id === liveContratoData.proyectoId)?.nombre || 'Proyecto'}
                  </span>
                )}
              </div>
            )}
            <h2 className="text-base sm:text-xl font-bold truncate">
              {titulo}
              {getFieldValue(liveContratoData?.numeroContrato, null) && getFieldValue(liveContratoData?.numeroContrato, null) !== 'N/A' && (
                <span className="text-sm sm:text-base font-normal opacity-80 ml-2">
                  ({getFieldValue(liveContratoData.numeroContrato)})
                </span>
              )}
            </h2>
            <p className="text-xs sm:text-sm opacity-90 truncate">
              {isConvenioContract && convenio
                ? `${convenio.nombre} — Beneficiario: ${beneficiariosById?.[contrato.beneficiarioId]?.nombre || 'N/A'}`
                : (clientData?.nombreEmpresa || 'Cliente')}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="h-[calc(100dvh-60px)] sm:h-[calc(100dvh-80px)] overflow-y-auto bg-zinc-50">
        <div className="max-w-7xl mx-auto p-4 sm:p-6">
          {/* Alert Message */}
          {alertMessage && (
            <div className={`mb-4 p-3 rounded-lg border ${
              alertType === 'success'
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              {alertMessage}
            </div>
          )}

          {/* Proveedor extraído vs cliente guardado (contratos de cliente) */}
          {!isConvenioContract && proveedor && clientData?.nombreEmpresa &&
            !namesMatch(proveedor, clientData.nombreEmpresa) && (
            <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <span className="font-semibold text-amber-800">
                El proveedor del contrato no coincide con el cliente
              </span>
              <span
                className="inline-block max-w-[16rem] truncate bg-white rounded px-2 py-0.5 border border-amber-200 text-gray-800"
                title={`Cliente: ${clientData.nombreEmpresa}`}
              >
                {clientData.nombreEmpresa}
              </span>
              <span className="text-amber-500 font-semibold">≠</span>
              <span
                className="inline-block max-w-[16rem] truncate bg-white rounded px-2 py-0.5 border border-amber-200 text-gray-800"
                title={`Contrato: ${proveedor}`}
              >
                {proveedor}
              </span>
            </div>
          )}

          {/* Two-column layout on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">

            {/* LEFT COLUMN - Contract Info, Client, Responsables */}
            <div className="space-y-4">

              {/* Cliente/Beneficiario Info - Compact */}
              <div className={`p-3 bg-white rounded-lg border-l-4 border border-gray-200 shadow-sm ${
                isConvenioContract ? 'border-l-[#53B5CC]' : 'border-l-azul-surelia'
              }`}>
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                  <UserIcon className={`w-4 h-4 ${isConvenioContract ? 'text-[#53B5CC]' : 'text-azul-surelia'}`} />
                  {isConvenioContract ? 'Beneficiario' : 'Cliente'}
                </h3>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  <div>
                    <div className="text-gray-500">{isConvenioContract ? 'Nombre' : 'Empresa'}</div>
                    <div className="text-gray-800 font-medium truncate">
                      {isConvenioContract
                        ? (beneficiariosById?.[contrato.beneficiarioId]?.nombre || 'N/A')
                        : (clientData?.nombreEmpresa || 'N/A')}
                    </div>
                  </div>
                  {isConvenioContract ? (
                    <>
                      <div>
                        <div className="text-gray-500">RFC</div>
                        <div className="text-gray-800 font-mono">{beneficiariosById?.[contrato.beneficiarioId]?.rfc || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Convenio</div>
                        <div className="text-gray-800 font-medium">{convenio?.nombre || 'N/A'}</div>
                      </div>
                      {liveContratoData?.proyectoId && (
                        <div>
                          <div className="text-gray-500">Proyecto</div>
                          <div className="text-gray-800 font-medium">
                            {(convenio?.proyectos || []).find((p) => p.id === liveContratoData.proyectoId)?.nombre || liveContratoData.proyectoId}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <div className="text-gray-500">Contacto</div>
                        <div className="text-gray-800">{clientData?.nombreContacto || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Teléfono</div>
                        <div className="text-gray-800">{clientData?.telefonoContacto || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Email</div>
                        <div className="text-gray-800 truncate">{clientData?.correoContacto || 'N/A'}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Proveedor (fiado) + su agente — convenio contracts only. The
                  proveedor obtains sus fianzas through its own agent; that
                  agent's identity comes from the emitted fianzas' XML. */}
              {isConvenioContract && (() => {
                const fianzasList = liveContratoData?.fianzas || [];
                const fiadoRfc = fianzasList.map((f) => f.xmlFianza?.fiado?.rfc).find(Boolean) || null;
                const seen = new Set();
                const agentes = [];
                fianzasList.forEach((f) => {
                  const a = f.xmlFianza?.agente;
                  if (!a || (!a.nombre && !a.rfc)) return;
                  const key = a.rfc || a.nombre;
                  if (seen.has(key)) return;
                  seen.add(key);
                  agentes.push(a);
                });
                return (
                  <div className="p-3 bg-white rounded-lg border-l-4 border-l-[#53B5CC] border border-gray-200 shadow-sm">
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                      <UserGroupIcon className="w-4 h-4 text-[#53B5CC]" />
                      Proveedor
                    </h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                      <div>
                        <div className="text-gray-500">Nombre (fiado)</div>
                        <div className="text-gray-800 font-medium truncate" title={proveedor || undefined}>
                          {proveedor || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">RFC</div>
                        <div className="text-gray-800 font-mono">{fiadoRfc || 'N/A'}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-500">
                          Agente del proveedor{agentes.length > 1 ? 'es' : ''}
                        </div>
                        {agentes.length === 0 ? (
                          <div
                            className="text-gray-400 italic"
                            title="El agente del proveedor aparece en el XML de la fianza emitida."
                          >
                            Se conocerá al registrar la fianza emitida
                          </div>
                        ) : (
                          agentes.map((a) => (
                            <div key={a.rfc || a.nombre} className="text-gray-800">
                              <span className="font-medium">{a.nombre || 'Sin nombre'}</span>
                              {a.rfc && <span className="ml-2 font-mono text-gray-500">{a.rfc}</span>}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Contract Details - Compact */}
              <div className={`p-3 bg-white rounded-lg border-l-4 border border-gray-200 shadow-sm ${
                estatus === 'activo' ? 'border-l-green-600' :
                estatus === 'terminado' ? 'border-l-blue-600' :
                estatus === 'cancelado' ? 'border-l-red-600' : 'border-l-gray-400'
              }`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                    <ClipboardDocumentListIcon className={`w-4 h-4 ${
                      estatus === 'activo' ? 'text-green-600' :
                      estatus === 'terminado' ? 'text-blue-600' :
                      estatus === 'cancelado' ? 'text-red-600' : 'text-gray-400'
                    }`} />
                    Contrato
                  </h3>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(estatus)}`}>
                    {estatus.charAt(0).toUpperCase() + estatus.slice(1)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  <div className="col-span-2">
                    <div className="text-gray-500">Título</div>
                    <div className="text-gray-800 font-medium">{titulo}</div>
                  </div>
                  {getFieldValue(liveContratoData.numeroContrato, null) && (
                    <div className="col-span-2">
                      <div className="text-gray-500">No. de Contrato</div>
                      <div className="text-gray-800 font-medium">{getFieldValue(liveContratoData.numeroContrato)}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-gray-500">Tipo</div>
                    <div className="text-gray-800">{tipo}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Monto</div>
                    <div className="text-azul-surelia font-semibold">{monto}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Beneficiario</div>
                    <div className="text-gray-800">{contratante}</div>
                  </div>
                  {proveedor && (
                    <div>
                      <div className="text-gray-500">Proveedor</div>
                      <div className="text-gray-800">{proveedor}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-gray-500">Vigencia</div>
                    <div className="text-gray-800">{fechaInicio} - {fechaTermino}</div>
                  </div>
                </div>

                {/* Error banner if contract processing had issues */}
                {getContractErrorMessage() && (
                  <div className="mt-2 p-2 rounded border border-red-200 bg-red-50 text-xs text-red-700 flex items-start gap-2">
                    <XCircleIcon className="w-3 h-3 mt-0.5 text-red-600 flex-shrink-0" />
                    <div className="break-words">{getContractErrorMessage()}</div>
                  </div>
                )}

                {liveContratoData.status === 'error' && (
                  <div className="mt-2 p-2 rounded border border-red-200 bg-red-50 text-xs text-red-700 flex items-start gap-2">
                    <XCircleIcon className="w-3 h-3 mt-0.5 text-red-600 flex-shrink-0" />
                    <div className="break-words">No pudimos procesar este contrato. Contacta a soporte.</div>
                  </div>
                )}

                {/* Ver and Reprocesar buttons (not while the file is still processing or failed) */}
                {(liveContratoData.fileUrl || hasProcesamiento)
                  && liveContratoData.status !== 'procesando'
                  && liveContratoData.status !== 'error' && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                    <Button
                      size="xs"
                      color="surelia"
                      outline
                      onClick={async () => {
                        try {
                          if (hasProcesamiento) {
                            // Convenio / directo contract: read from procesamiento subcollection
                            let fileData = contractFileData;
                            if (!fileData) {
                              // Fetch on demand if not yet loaded
                              const procQuery = query(
                                collection(db, 'contracts', liveContratoData.id, 'procesamiento'),
                                orderBy('uploadedAt', 'desc'),
                                limit(1)
                              );
                              const procSnap = await getDocs(procQuery);
                              if (!procSnap.empty) {
                                const procDoc = procSnap.docs[0];
                                fileData = { ...procDoc.data(), fileDocId: procDoc.id };
                                setContractFileData(fileData);
                              }
                            }
                            if (!fileData?.storagePath) {
                              alert('No se encontró el archivo del contrato.');
                              return;
                            }
                            const pdfUrl = contractPdfUrl || await getDownloadURL(ref(storage, fileData.storagePath));
                            setProcessedViewData({
                              processedFileData: fileData,
                              fileType: 'contrato',
                              fileName: fileData.fileName || titulo,
                              fileLabel: 'Contrato',
                              pdfUrl,
                              contractId: liveContratoData.id,
                            });
                            setIsProcessedViewOpen(true);
                          } else {
                            // Link-based contract
                            if (!liveContratoData.fileUrl) {
                              alert('No hay archivo de contrato disponible.');
                              return;
                            }
                            const timestamp = liveContratoData.id.split('_')[1];
                            if (!timestamp) {
                              alert('No se pudo obtener el ID del archivo.');
                              return;
                            }
                            const fileDocId = `contrato_${timestamp}`;
                            const fileDocRef = doc(db, 'links', liveContratoData.linkId, 'files', fileDocId);
                            const fileDocSnap = await getDoc(fileDocRef);
                            if (!fileDocSnap.exists()) {
                              alert('No se encontró el archivo del contrato.');
                              return;
                            }
                            const contractFile = fileDocSnap.data();
                            const pdfUrl = contractPdfUrl || await getDownloadURL(ref(storage, liveContratoData.fileUrl));
                            setProcessedViewData({
                              processedFileData: { ...contractFile, fileDocId: fileDocId },
                              fileType: 'contrato',
                              fileName: contractFile.name || titulo,
                              fileLabel: 'Contrato',
                              pdfUrl,
                              linkId: liveContratoData.linkId,
                            });
                            setIsProcessedViewOpen(true);
                          }
                        } catch (error) {
                          console.error('Error loading PDF:', error);
                          alert('No se pudo cargar el archivo del contrato.');
                        }
                      }}
                    >
                      Ver
                    </Button>
                    {!isClient && !hasProcesamiento && (liveContratoData.fianzas?.length ?? 0) === 0 && (
                      <Button
                        size="xs"
                        color="surelia"
                        outline
                        onClick={handleReprocessContract}
                        disabled={isReprocessing}
                        className="ml-auto"
                      >
                        {isReprocessing ? 'Reprocesando...' : 'Reprocesar'}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Beneficiario con convenio — contract-level marker: every
                  fianza nets the fiado share. Hidden on convenio contracts
                  (already the other side of a split). */}
              {!isClient && !isConvenioContract && (
              <div className={`p-3 bg-white rounded-lg border-l-4 border border-gray-200 shadow-sm ${
                liveContratoData?.beneficiarioConvenio ? 'border-l-azul-surelia' : 'border-l-gray-400'
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2 min-w-0">
                    <BanknotesIcon className={`w-4 h-4 flex-shrink-0 ${
                      liveContratoData?.beneficiarioConvenio ? 'text-azul-surelia' : 'text-gray-400'
                    }`} />
                    <span className="truncate">
                      {liveContratoData?.beneficiarioConvenio
                        ? 'Beneficiario con convenio'
                        : 'Sin convenio del beneficiario'}
                    </span>
                  </h3>
                  {userData?.rol === 'owner' && (
                    <button
                      onClick={handleToggleBeneficiarioConvenio}
                      disabled={savingBeneficiarioConvenio}
                      title="Aplica a todas las fianzas del contrato y recalcula sus comisiones pendientes"
                      className="text-xs text-azul-surelia hover:underline font-medium disabled:opacity-50 flex-shrink-0"
                    >
                      {savingBeneficiarioConvenio
                        ? 'Guardando...'
                        : liveContratoData?.beneficiarioConvenio ? 'Quitar' : 'Marcar'}
                    </button>
                  )}
                </div>
              </div>
              )}

              {/* Responsables del Contrato - Compact (hidden for client users) */}
              {!isClient && (
              <div className="p-3 bg-white rounded-lg border-l-4 border-l-purple-600 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                    <UserGroupIcon className="w-4 h-4 text-purple-600" />
                    Responsables
                  </h3>
                  <button
                    onClick={() => setIsContractAssignmentOpen(true)}
                    className="text-xs text-azul-surelia hover:underline font-medium"
                  >
                    + Agregar
                  </button>
                </div>
                {(clientData?.assignedUsers?.length > 0 || liveContratoData?.assignedUsers?.length > 0) ? (
                  <div className="flex items-center gap-2">
                    <AssignedUsersBubbles
                      userIds={[...(clientData?.assignedUsers || []), ...(liveContratoData?.assignedUsers || [])].filter((v, i, a) => a.indexOf(v) === i)}
                      maxDisplay={4}
                      size="sm"
                    />
                    {liveContratoData?.assignedUsers?.length > 0 && (
                      <span className="text-[10px] text-gray-500">
                        ({liveContratoData.assignedUsers.length} en contrato)
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400 text-xs italic">Sin responsables asignados</span>
                )}
              </div>
              )}
            </div>

            {/* RIGHT COLUMN - Fianzas Requeridas y Emitidas */}
            <div className="space-y-4">

            {/* Fianzas Requeridas */}
            <div className="p-3 bg-white rounded-lg border-l-4 border-l-azul-surelia border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                  <ShieldCheckIcon className="w-4 h-4 text-azul-surelia" />
                  Requeridas ({requiredEditable.length})
                </h3>
                {!isClient && (
                  <div className="flex gap-1">
                    <button
                      onClick={handleAddRequired}
                      disabled={savingRequired || editingIndex !== null}
                      className="text-xs text-azul-surelia hover:underline font-medium disabled:opacity-50"
                    >
                      + Agregar
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setIsUploadFianzaOpen(true)}
                      className="text-xs text-azul-surelia hover:underline font-medium"
                    >
                      Subir
                    </button>
                  </div>
                )}
              </div>
              {requiredEditable.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {requiredEditable.map((fianzaReq, idx) => {
                    const isEditing = editingIndex === idx;
                    return (
                    <div
                      key={idx}
                      className="bg-gray-50 rounded border border-gray-200 p-2"
                    >
                      <div className="flex justify-between items-start gap-1 mb-1.5">
                        <input
                          className="flex-1 min-w-0 text-xs font-semibold text-azul-surelia bg-transparent border-b border-azul-surelia/40 focus:outline-none focus:border-azul-surelia truncate"
                          value={fianzaReq.tipo || ""}
                          placeholder="Tipo"
                          disabled={!isEditing}
                          onChange={(e) => handleRequiredChange(idx, "tipo", e.target.value)}
                        />
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isEditing ? (
                            <select
                              className="text-[10px] border rounded px-1 py-0.5"
                              value={fianzaReq.estatus || "pendiente"}
                              onChange={(e) => handleRequiredChange(idx, "estatus", e.target.value)}
                            >
                              <option value="pendiente">Pendiente</option>
                              <option value="en_proceso">En Proceso</option>
                              <option value="emitida">Emitida</option>
                              <option value="postergada">Postergada</option>
                            </select>
                          ) : (
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${getFianzaStatusColor(fianzaReq.estatus || 'pendiente')}`}
                            >
                              {(fianzaReq.estatus || 'pendiente').charAt(0).toUpperCase() + (fianzaReq.estatus || 'pendiente').slice(1)}
                            </span>
                          )}
                          {!isEditing && !isClient && (
                            <button
                              onClick={() => {
                                if (editingIndex !== null) return;
                                setEditingIndex(idx);
                              }}
                              className="text-azul-surelia hover:text-azul-surelia/80 p-0.5"
                              title="Editar"
                            >
                              <PencilSquareIcon className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-[10px]">
                        <div>
                          <span className="text-gray-500">%:</span>
                          {isEditing ? (
                            <input
                              type="number"
                              className="w-full text-xs border rounded px-1 py-0.5 mt-0.5"
                              value={fianzaReq.porcentaje || ""}
                              onChange={(e) => handleRequiredChange(idx, "porcentaje", e.target.value)}
                            />
                          ) : (
                            <span className="ml-1 font-semibold text-gray-800">
                              {fianzaReq.porcentaje !== undefined && fianzaReq.porcentaje !== ""
                                ? `${fianzaReq.porcentaje}%`
                                : 'N/A'}
                            </span>
                          )}
                        </div>
                        <div>
                          <span className="text-gray-500">Monto:</span>
                          {isEditing ? (
                            <input
                              type="number"
                              className="w-full text-xs border rounded px-1 py-0.5 mt-0.5"
                              value={fianzaReq.monto || ""}
                              onChange={(e) => handleRequiredChange(idx, "monto", e.target.value)}
                            />
                          ) : (
                            <span className="ml-1 font-semibold text-azul-surelia text-xs">
                              {fianzaReq.monto !== undefined && fianzaReq.monto !== ""
                                ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(fianzaReq.monto))
                                : 'N/A'}
                            </span>
                          )}
                        </div>
                        {/* Momento field */}
                        <div className="col-span-2 mt-1">
                          <span className="text-gray-500">Momento:</span>
                          {isEditing ? (
                            <select
                              className="w-full text-xs border rounded px-1 py-0.5 mt-0.5"
                              value={fianzaReq.momento || "antes_inicio"}
                              onChange={(e) => handleRequiredChange(idx, "momento", e.target.value)}
                            >
                              <option value="antes_inicio">Antes del inicio</option>
                              <option value="durante">Durante</option>
                              <option value="despues_termino">Después del término</option>
                            </select>
                          ) : (
                            <span className="ml-1 text-gray-700">
                              {fianzaReq.momento === 'durante' ? 'Durante' :
                               fianzaReq.momento === 'despues_termino' ? 'Después del término' :
                               'Antes del inicio'}
                            </span>
                          )}
                        </div>
                        {/* PostergadaHasta date picker - only shown when estatus is postergada */}
                        {(isEditing && fianzaReq.estatus === 'postergada') && (
                          <div className="col-span-2 mt-1">
                            <span className="text-gray-500">Recordar el:</span>
                            <input
                              type="date"
                              className="w-full text-xs border rounded px-1 py-0.5 mt-0.5"
                              value={fianzaReq.postergadaHasta ? new Date(fianzaReq.postergadaHasta).toISOString().split('T')[0] : ''}
                              onChange={(e) => handleRequiredChange(idx, "postergadaHasta", e.target.value ? new Date(e.target.value).getTime() : null)}
                            />
                          </div>
                        )}
                        {(!isEditing && fianzaReq.estatus === 'postergada' && fianzaReq.postergadaHasta) && (
                          <div className="col-span-2 mt-1">
                            <span className="text-gray-500">Recordar el:</span>
                            <span className="ml-1 text-purple-700 font-medium">
                              {new Date(fianzaReq.postergadaHasta).toLocaleDateString('es-MX')}
                            </span>
                          </div>
                        )}
                        {/* Link indicator when fianzaId is set */}
                        {fianzaReq.fianzaId && (
                          <div className="col-span-2 mt-1">
                            <span className="text-green-600 text-[10px] flex items-center gap-1">
                              <CheckBadgeIcon className="w-3 h-3" />
                              Vinculada a fianza emitida
                            </span>
                          </div>
                        )}
                      </div>
                      {isEditing && (
                        <div className="flex justify-between items-center gap-1 mt-2 pt-2 border-t border-gray-200">
                          <button
                            onClick={() => handleDeleteRequired(idx)}
                            className="text-[10px] text-red-600 hover:underline"
                          >
                            Eliminar
                          </button>
                          <div className="flex gap-1">
                            <button
                              onClick={handleCancelRequired}
                              className="px-2 py-0.5 text-[10px] rounded border border-gray-300 hover:bg-gray-100"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={handleSaveRequired}
                              disabled={savingRequired}
                              className="px-2 py-0.5 text-[10px] rounded bg-azul-surelia text-white hover:bg-azul-surelia/90 disabled:opacity-50"
                            >
                              {savingRequired ? "..." : "Guardar"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-400 text-xs">
                  No hay fianzas requeridas
                </div>
              )}
            </div>

            {/* Fianzas Emitidas */}
            <div className="p-3 bg-white rounded-lg border-l-4 border-l-emerald-600 border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2 mb-3">
                <CheckBadgeIcon className="w-4 h-4 text-emerald-600" />
                Emitidas ({fianzasEmitidas.length})
              </h3>
              {fianzasEmitidas.length > 0 ? (
                <div className="space-y-2">
                  {fianzasEmitidas.map((fianza, idx) => {
                    const estatusCalculado = getFianzaStatus(fianza).code;

                    return (
                      <div
                        key={fianza.id || idx}
                        className="bg-gray-50 border border-gray-200 rounded p-2 hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => {
                          setSelectedFianza(fianza);
                          setIsFianzaViewOpen(true);
                        }}
                      >
                        {(() => {
                          const contractProveedor = liveContratoData.proveedor?.value || '';
                          const fiadoXmlNombre = fianza.xmlFianza?.fiado?.nombre || '';
                          const proveedorMismatch = contractProveedor && fiadoXmlNombre && !namesMatch(contractProveedor, fiadoXmlNombre);
                          return (
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs font-semibold text-gray-800 truncate">{fianza.tipo || fianza.tipoFianza || 'N/A'}</span>
                              <span className="text-[10px] text-gray-400">Vigencia:</span>
                              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${getFianzaStatusColor(estatusCalculado)}`}>
                                {estatusCalculado.charAt(0).toUpperCase() + estatusCalculado.slice(1)}
                              </span>
                              {fianza.pagada !== undefined && (
                                <>
                                  <span className="text-[10px] text-gray-400">Pago:</span>
                                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                    fianza.pagada ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                                  }`}>
                                    {fianza.pagada ? 'Pagada' : 'Pendiente'}
                                  </span>
                                </>
                              )}
                              {fianza.renovable && (
                                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-verde-lima text-azul-surelia">
                                  Renovable
                                </span>
                              )}
                              {proveedorMismatch && (
                                <span
                                  title={`Fiado XML: ${fiadoXmlNombre}. Prevalece el proveedor del contrato (${contractProveedor}).`}
                                  className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800"
                                >
                                  Fiado no coincide
                                </span>
                              )}
                            </div>
                            {fianza.numeroFianza && (
                              <div className="text-[10px] text-gray-500 font-mono truncate">
                                Póliza: {formatNumeroFianza(fianza)}
                              </div>
                            )}
                            {proveedorMismatch && (
                              <div className="text-[10px] text-amber-700 mt-0.5 truncate" title={fiadoXmlNombre}>
                                XML: {fiadoXmlNombre}
                              </div>
                            )}
                          </div>
                          {!isConvenioContract && (contrato?.clientId || clientData?.id) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setNotifyFianza({
                                  contratoId: fianza.contratoId || contrato?.id,
                                  fianzaId: fianza.id,
                                  numeroFianza: fianza.numeroFianza || '',
                                  filePaths: fianza.filePaths,
                                  eventos: fianza.eventos,
                                });
                              }}
                              className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-colors flex-shrink-0"
                              title="Enviar fianza por correo"
                              aria-label="Enviar fianza por correo"
                            >
                              <EnvelopeIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                          );
                        })()}

                        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
                          {fianza.montoAfianzado && (
                            <div>
                              <span className="text-gray-500">Afianzado:</span>
                              <span className="ml-1 font-semibold text-gray-800">
                                {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fianza.montoAfianzado)}
                              </span>
                            </div>
                          )}
                          {fianza.monto && (
                            <div>
                              <span className="text-gray-500">Prima:</span>
                              <span className="ml-1 font-semibold text-gray-800">
                                {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fianza.monto)}
                              </span>
                            </div>
                          )}
                          {fianza.tarifa && hasTarifa && (
                            <div>
                              <span className="text-gray-500">Tarifa:</span>
                              <span className="ml-1 font-semibold text-gray-800">{fianza.tarifa.toFixed(2)}%</span>
                            </div>
                          )}
                          {fianza.inicioVigenciaFianza && (
                            <div>
                              <span className="text-gray-500">Vigencia:</span>
                              <span className="ml-1 text-gray-800">
                                {new Date(fianza.inicioVigenciaFianza).toLocaleDateString('es-MX')} - {fianza.finVigenciaFianza ? new Date(fianza.finVigenciaFianza).toLocaleDateString('es-MX') : 'N/A'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-400 text-xs">
                  No hay fianzas emitidas
                </div>
              )}
            </div>

            </div>
          </div>

          {/* Documentos del contrato (broker-only attach + terminal events) */}
          {!isClient && (
            <ContratoDocumentos
              contratoData={liveContratoData}
              clientData={clientData}
              convenio={convenio}
              user={user}
              userData={userData}
              onUpdate={onUpdate}
            />
          )}

          {/* Lo que falta de ESTE contrato: actas de entrega y convenios
              modificatorios viven a nivel contrato (el tipo dicta el nivel).
              Own card above the historial, same split as in FianzaView: the
              historial is what already happened, this is what is still open. */}
          {!isClient && liveContratoData?.id && (
            <div className="mb-4">
              <OtActivas contratoId={liveContratoData.id} titulo="Órdenes de trabajo del contrato" onNavigate={onClose} />
            </div>
          )}
          {!isClient && liveContratoData?.id && (
            <div className="mb-4">
              <PendientesPanel
                scope={{ contratoId: liveContratoData.id }}
                correoFiado
                clientName={clientData?.nombreEmpresa || ''}
                titulo="Pendientes del contrato"
                nuevoLabel="Registrar"
                vacio="linea"
                tarjeta
                Icon={ClipboardDocumentListIcon}
                tipos={[PENDIENTE_TIPOS.ACTA_ENTREGA, PENDIENTE_TIPOS.CONVENIO_MODIFICATORIO]}
                preset={{
                  clienteId: clientData?.id || contrato?.clientId || null,
                  convenioId: liveContratoData?.convenioId || null,
                  contratoId: liveContratoData.id,
                  contratoTitulo: liveContratoData?.tituloContrato?.value || '',
                }}
              />
            </div>
          )}

          {/* Historial unificado del contrato (endosos + documentos) */}
          <ContratoTimeline contratoData={liveContratoData} />
        </div>
      </div>

      {/* Upload Fianza Overlay */}
      {isUploadFianzaOpen && (
        <UploadFianza
          isOpen={isUploadFianzaOpen}
          onClose={() => setIsUploadFianzaOpen(false)}
          clientId={isConvenioContract ? undefined : clientData?.id}
          clientName={isConvenioContract ? undefined : clientData?.nombreEmpresa}
          clientRfc={isConvenioContract ? undefined : clientData?.rfc}
          linkId={isConvenioContract ? undefined : liveContratoData.linkId}
          contratoId={liveContratoData.id}
          contratoProveedor={isConvenioContract ? (liveContratoData.proveedor?.value || '') : ''}
          convenioId={isConvenioContract ? liveContratoData.convenioId : undefined}
          convenioName={isConvenioContract ? convenio?.nombre : undefined}
          pendingFianzas={fianzasRequeridas
            .map((f, idx) => ({ ...f, __index: idx }))
            .filter(f => !f.pagada && (f.estatus === 'pendiente' || !f.estatus))}
          onSuccess={() => {
            // Do NOT close here: UploadFianza still shows the notify-by-email
            // step after saving and closes itself via onClose when done.
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {/* Quick-send fianza by email */}
      <NotifyFianzaDialog
        open={!!notifyFianza}
        onClose={() => setNotifyFianza(null)}
        clientId={contrato?.clientId || clientData?.id}
        clientName={clientData?.nombreEmpresa}
        contratoId={notifyFianza?.contratoId}
        fianzaId={notifyFianza?.fianzaId}
        numeroFianza={notifyFianza?.numeroFianza}
        filePaths={notifyFianza?.filePaths}
        eventos={notifyFianza?.eventos}
      />

      {/* Fianza View Overlay */}
      {isFianzaViewOpen && selectedFianza && (
        <FianzaView
          isOpen={isFianzaViewOpen}
          onClose={() => {
            setIsFianzaViewOpen(false);
            setSelectedFianza(null);
          }}
          fianza={selectedFianza}
          clientData={clientData}
          contratoData={liveContratoData}
          convenio={convenio}
          beneficiariosById={beneficiariosById}
          onUpdate={() => {
            if (onUpdate) onUpdate();
          }}
        />
      )}

      {/* Processed File View Overlay */}
      {isProcessedViewOpen && (
        <ProcessedFileView
          isOpen={isProcessedViewOpen}
          onClose={() => setIsProcessedViewOpen(false)}
          processedFileData={processedViewData.processedFileData}
          fileType={processedViewData.fileType}
          fileName={processedViewData.fileName?.length > 25 ? processedViewData.fileName.substring(0, 80) + '...' : processedViewData.fileName}
          fileLabel={processedViewData.fileLabel}
          pdfUrl={processedViewData.pdfUrl}
          linkId={processedViewData.linkId}
          contractId={processedViewData.contractId}
          convenioId={isConvenioContract ? liveContratoData.convenioId : undefined}
          convenioName={isConvenioContract ? convenio?.nombre : undefined}
          clientName={isConvenioContract ? undefined : clientData?.nombreEmpresa}
          clientRfc={isConvenioContract ? undefined : clientData?.rfc}
          bypassReview={isClient || isConvenioContract}
          onMarkAsReviewed={async (idOrLinkId, fileDocId) => {
            if (hasProcesamiento) {
              // Convenio / directo: update procesamiento subcollection
              const procRef = doc(db, 'contracts', idOrLinkId, 'procesamiento', fileDocId);
              await updateDoc(procRef, {
                reviewedBy: 'Usuario',
                reviewedAt: new Date()
              });
            } else {
              // Link-based: update links files subcollection
              const fileDocRef = doc(db, 'links', idOrLinkId, 'files', fileDocId);
              await updateDoc(fileDocRef, {
                reviewedBy: 'Usuario',
                reviewedAt: new Date()
              });
            }
          }}
          onUnmarkAsReviewed={async (idOrLinkId, fileDocId) => {
            if (hasProcesamiento) {
              const procRef = doc(db, 'contracts', idOrLinkId, 'procesamiento', fileDocId);
              await updateDoc(procRef, {
                reviewedBy: null,
                reviewedAt: null
              });
            } else {
              const fileDocRef = doc(db, 'links', idOrLinkId, 'files', fileDocId);
              await updateDoc(fileDocRef, {
                reviewedBy: null,
                reviewedAt: null
              });
            }
          }}
          onRefresh={async () => {
            await loadContractFileData();
          }}
          clientId={clientData?.id}
        />
      )}

      {/* Contract User Assignment Dialog */}
      <Dialog
        open={isContractAssignmentOpen}
        onClose={() => setIsContractAssignmentOpen(false)}
        size="md"
      >
        <div className="p-1">
          <Heading level={3} className="text-xl font-bold text-azul-surelia mb-2">
            Agregar Responsables al Contrato
          </Heading>
          <Text className="text-sm text-gray-500 mb-4">
            Agrega usuarios adicionales como responsables de este contrato específico.
          </Text>
          <UserAssignmentSelector
            assignedUsers={liveContratoData?.assignedUsers || []}
            onSave={async (userIds) => {
              await updateContractAssignedUsers(contrato.id, userIds);
              setIsContractAssignmentOpen(false);
            }}
            onCancel={() => setIsContractAssignmentOpen(false)}
          />
        </div>
      </Dialog>
    </div>
  );
}
